
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, CheckCircle, User, CreditCard, Building, MessageSquare } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { Checkbox } from '@/components/ui/checkbox';

interface PaymentInstructionsProps {
  confirmationCode: string;
  hasCheckedPayment?: boolean;
  onPaymentConfirmChange?: (confirmed: boolean) => void;
  paymentConfirmed?: boolean;
}

const PaymentInstructions: React.FC<PaymentInstructionsProps> = ({ 
  confirmationCode,
  hasCheckedPayment = false,
  onPaymentConfirmChange,
  paymentConfirmed = false
}) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Copy text to clipboard and show toast notification
  const copyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopiedField(fieldName);
        toast({
          title: "Đã sao chép!",
          description: `${fieldName} đã được sao chép vào clipboard.`,
          duration: 2000,
        });
        
        // Reset the copied state after 2 seconds
        setTimeout(() => {
          setCopiedField(null);
        }, 2000);
      })
      .catch(err => {
        console.error('Không thể sao chép: ', err);
        toast({
          title: "Lỗi sao chép",
          description: "Không thể sao chép. Vui lòng thử lại.",
          variant: "destructive",
        });
      });
  };

  const paymentDetails = [
    { 
      icon: <User size={16} className="text-blue-600" />, 
      label: "Chủ tài khoản", 
      value: "Ho Minh Nhut", 
      id: "account-name" 
    },
    { 
      icon: <CreditCard size={16} className="text-blue-600" />, 
      label: "Số tài khoản", 
      value: "19033779674017", 
      id: "account-number" 
    },
    { 
      icon: <Building size={16} className="text-blue-600" />, 
      label: "Ngân hàng", 
      value: "TMCP Techcombank", 
      id: "bank-name" 
    },
  ];

  const handlePaymentConfirmed = (checked: boolean) => {
    if (onPaymentConfirmChange) {
      onPaymentConfirmChange(checked);
    }
  };

  return (
    <motion.div 
      className="w-full border-t pt-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.5 }}
    >
      {!hasCheckedPayment ? (
        <>
          <p className="font-semibold text-blue-700 mb-3">Thông tin chuyển khoản:</p>
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-3 gap-3'} items-start`}>
            <div className={`space-y-3 ${isMobile ? '' : 'col-span-2'}`}>
              {paymentDetails.map((detail) => (
                <div 
                  key={detail.id}
                  className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3 group hover:border-blue-300 hover:bg-blue-50 transition-all duration-200"
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    {detail.icon}
                    <span className="text-gray-600 whitespace-nowrap">{detail.label}:</span>
                    <span className="font-medium truncate">{detail.value}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(detail.value, detail.label)}
                    className="text-gray-400 hover:text-blue-600 transition-colors p-1 rounded-md hover:bg-blue-100 flex-shrink-0"
                    aria-label={`Sao chép ${detail.label}`}
                  >
                    {copiedField === detail.label ? (
                      <CheckCircle size={18} className="text-green-500" />
                    ) : (
                      <Copy size={18} />
                    )}
                  </button>
                </div>
              ))}
              
              {/* Reference Code (Nội dung) - Highlighted */}
              <div 
                className="flex items-center justify-between bg-amber-50 border-2 border-amber-300 rounded-lg p-3 group hover:border-amber-400 transition-all duration-200"
              >
                <div className="flex items-center gap-2 overflow-hidden">
                  <MessageSquare size={16} className="text-amber-600 flex-shrink-0" />
                  <span className="text-amber-800 whitespace-nowrap">Nội dung:</span>
                  <span className="font-bold text-amber-900 truncate">{confirmationCode}</span>
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(confirmationCode, "Nội dung chuyển khoản")}
                  className="text-amber-600 hover:text-amber-800 transition-colors p-1 rounded-md hover:bg-amber-200 flex-shrink-0"
                  aria-label="Sao chép nội dung chuyển khoản"
                >
                  {copiedField === "Nội dung chuyển khoản" ? (
                    <CheckCircle size={18} className="text-green-600" />
                  ) : (
                    <Copy size={18} />
                  )}
                </button>
              </div>
            </div>
            
            <div className={`flex flex-col items-center ${isMobile ? 'mt-4' : ''}`}>
              <div className="relative mb-4">
                <img 
                  src="/lovable-uploads/4ec94dc7-20e8-44dc-82b8-02d3e24f51b5.png" 
                  alt="QR Code thanh toán" 
                  className="max-w-[180px] border rounded-md shadow-sm bg-white p-2"
                />
                <div className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-xs rounded-full px-2 py-0.5 shadow-sm">
                  QR Code
                </div>
              </div>
              
              {/* Payment Confirmation Checkbox - moved below QR code */}
              <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-3 w-full">
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="payment-confirmed" 
                    checked={paymentConfirmed}
                    onCheckedChange={handlePaymentConfirmed}
                    className="mt-0.5 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                  />
                  <div className="grid gap-1">
                    <label
                      htmlFor="payment-confirmed"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Tôi đã chuyển khoản
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Vui lòng xác nhận bạn đã chuyển khoản theo thông tin trên trước khi tiếp tục.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3 text-center">Quét mã QR để thanh toán nhanh hoặc sao chép thông tin để chuyển khoản thủ công</p>
        </>
      ) : (
        <div className="text-center py-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 mb-3"
          >
            <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-green-800">Xác nhận thanh toán thành công!</h3>
            <p className="text-green-700">Cảm ơn bạn đã hoàn tất quá trình đăng ký.</p>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default PaymentInstructions;
